/**
 * Bug sheets: Google Sheets of bugs that Switchboard polls. Each open bug whose Branch
 * column is mapped to a codebase goes into that codebase's session of the day, on a daily
 * branch (bug-fix/DD-MM-YY), and its report lands in the sheet's report sheet.
 */

/** Header names, matched case-insensitively and ignoring extra whitespace. */
export interface BugSheetColumns {
  /** Bug sheet: the bug's number or ID (the key; one fix per bug). */
  bug: string;
  status: string;
  /** The branch the fix is for; mapped to a codebase on the dashboard. */
  branch: string;
  title: string;
  /** Report sheet: where the bug number, the report and the PR link go. */
  reportBug: string;
  reportText: string;
  reportPr: string;
}

export const DEFAULT_BUG_COLUMNS: BugSheetColumns = {
  bug: "Bug",
  status: "Status",
  branch: "Branch",
  title: "Title",
  reportBug: "Bug Number",
  reportText: "bug report",
  reportPr: "PR",
};

export const DEFAULT_BUG_BRANCH_PATTERN = "bug-fix/{DD}-{MM}-{YY}";

/** queued: picked, not yet sent to Claude · working: sent · reported: Claude wrote its report, being published · done: in the report sheet. */
export const BUG_TASK_STATUSES = ["queued", "working", "reported", "done", "failed"] as const;
export type BugTaskStatus = (typeof BUG_TASK_STATUSES)[number];

export const BUG_OUTCOMES = ["fixed", "not_fixed", "needs_info"] as const;
export type BugOutcome = (typeof BUG_OUTCOMES)[number];

export const BUG_OUTCOME_LABELS: Record<BugOutcome, string> = {
  fixed: "Fixed",
  not_fixed: "Not fixed",
  needs_info: "Needs info",
};

export interface BugPollSummary {
  rows: number;
  /** Rows whose status is the sheet's open status. */
  open: number;
  /** Bugs picked up by this poll. */
  picked: number;
  /** Open bugs with a branch that has no codebase mapped, by branch. */
  unmapped: Record<string, number>;
  /** Open bugs with no branch selected (left alone). */
  noBranch: number;
}

/** Normalises a header or a cell for matching: trimmed, whitespace collapsed, lower case. */
export function normCell(v: string | undefined | null): string {
  return (v ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

/** `https://docs.google.com/spreadsheets/d/<id>/edit?gid=<gid>#gid=<gid>` → its ID and tab. */
export function parseSheetUrl(url: string): { spreadsheetId: string; gid?: string } | null {
  const m = url.trim().match(/\/spreadsheets\/d\/([A-Za-z0-9_-]{10,})/);
  if (!m) return null;
  const gid = url.match(/[#?&]gid=(\d+)/)?.[1];
  return { spreadsheetId: m[1]!, ...(gid ? { gid } : {}) };
}

export function isTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** The calendar date at `date` in `timeZone`. */
export function dayParts(date: Date, timeZone: string): { YYYY: string; YY: string; MM: string; DD: string } {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const YYYY = get("year");
  return { YYYY, YY: YYYY.slice(-2), MM: get("month"), DD: get("day") };
}

/** `YYYY-MM-DD` in `timeZone`. */
export function dayKey(date: Date, timeZone: string): string {
  const d = dayParts(date, timeZone);
  return `${d.YYYY}-${d.MM}-${d.DD}`;
}

/** The day's branch: `bug-fix/{DD}-{MM}-{YY}` → `bug-fix/29-10-26`. */
export function dailyBranch(pattern: string, date: Date, timeZone: string): string {
  const d = dayParts(date, timeZone);
  return pattern.replace(/\{(YYYY|YY|MM|DD)\}/g, (_, k: keyof typeof d) => d[k]);
}

/** Problems with a branch pattern; git itself checks the result on use. */
export function branchPatternProblem(pattern: string): string | undefined {
  if (!/\{DD\}/.test(pattern)) return "the pattern needs {DD} so each day gets its own branch";
  const sample = pattern.replace(/\{(YYYY|YY|MM|DD)\}/g, "01");
  if (/\{[^}]*\}/.test(sample)) return "unknown placeholder; use {DD}, {MM}, {YY} or {YYYY}";
  if (!/^[A-Za-z0-9._\/-]+$/.test(sample) || sample.startsWith("/") || sample.endsWith("/") || sample.includes("..") || sample.includes("//")) {
    return "letters, digits, '.', '_', '-' and '/' only";
  }
  return undefined;
}
